import type { GuidanceResult } from '../lib/guidance-engine'
import type { AnswerMap, AnswerValue, PhaseInfo, WorkflowStage } from '../types/workflow'
import {
  AddressFormTemplate,
  BasicsTemplate,
  BmiCalculatorTemplate,
  BmiEducationTemplate,
  BodyPhotoTemplate,
  CheckboxListTemplate,
  ComorbidityTemplate,
  DosageTemplate,
  EmailCaptureTemplate,
  ExperienceTemplate,
  GoalsTemplate,
  IdUploadTemplate,
  IntroTemplate,
  LastDoseTemplate,
  LifeContextTemplate,
  MedicationAllergiesPickerTemplate,
  MedicineSelectionTemplate,
  PastGlp1Template,
  PaymentMethodTemplate,
  RadioCardsTemplate,
  RadioListTemplate,
  ReviewSubmitTemplate,
  SafetyScreeningTemplate,
  SmartMedicationPickerTemplate,
  TextAreaTemplate,
  TherapyPreviewTemplate,
  WhichMedicationsTemplate,
} from './templates'

interface StageRendererProps {
  stage: WorkflowStage | null
  answers: AnswerMap
  getAnswer: (field: string) => AnswerValue
  setAnswer: (field: string, value: AnswerValue) => void
  setMultipleAnswers: (answers: Record<string, AnswerValue>) => void
  guidanceData?: GuidanceResult
  onContinue: () => void
  onSubmit?: () => Promise<void>
  currentPhase?: PhaseInfo | null
}

export default function StageRenderer({
  stage,
  answers,
  getAnswer,
  setAnswer,
  setMultipleAnswers,
  guidanceData,
  onContinue,
  onSubmit,
  currentPhase,
}: StageRendererProps) {
  if (!stage) return <div className="py-10 text-center text-white/60">Loading...</div>

  const initialValue = stage.answerMapping?.field ? getAnswer(stage.answerMapping.field) : undefined
  const handleSelect = (value: AnswerValue) => {
    if (stage.answerMapping?.field) {
      setAnswer(stage.answerMapping.field, value)
    }
  }

  const handleSelectObject = (value: AnswerValue) => {
    if (value && typeof value === 'object' && !Array.isArray(value)) {
      setMultipleAnswers(value as Record<string, AnswerValue>)
    } else {
      handleSelect(value)
    }
  }

  switch (stage.stageTemplate) {
    case 'intro':
      return <IntroTemplate stage={stage} onContinue={onContinue} />
    case 'radio_list':
      return (
        <RadioListTemplate
          stage={stage}
          initialValue={initialValue as string}
          onSelect={(value) => {
            handleSelect(value)
            if (stage.id === 'ww-glp1-experience') {
              onContinue()
            }
          }}
          currentPhase={currentPhase}
        />
      )
    case 'radio_cards':
      return <RadioCardsTemplate stage={stage} initialValue={initialValue as string} onSelect={handleSelect as (v: string) => void} currentPhase={currentPhase} />
    case 'which_medications':
      return (
        <WhichMedicationsTemplate
          stage={stage}
          answers={answers}
          currentPhase={currentPhase}
          onSelect={(value) => {
            handleSelectObject(value)
            onContinue()
          }}
        />
      )
    case 'dosage':
      return (
        <DosageTemplate
          stage={stage}
          answers={answers}
          currentPhase={currentPhase}
          onSelect={(value) => {
            handleSelectObject(value)
            onContinue()
          }}
        />
      )
    case 'experience':
      return (
        <ExperienceTemplate
          stage={stage}
          answers={answers}
          currentPhase={currentPhase}
          onSelect={(value) => {
            handleSelectObject(value)
            onContinue()
          }}
        />
      )
    case 'last_dose':
      return (
        <LastDoseTemplate
          stage={stage}
          answers={answers}
          currentPhase={currentPhase}
          onSelect={(value) => {
            handleSelectObject(value)
            onContinue()
          }}
        />
      )
    case 'checkbox_list':
      return <CheckboxListTemplate stage={stage} initialValue={initialValue as string[]} onSelect={handleSelect as (v: string[]) => void} currentPhase={currentPhase} />
    case 'text_area':
      return (
        <TextAreaTemplate
          stage={stage}
          initialValue={initialValue as string}
          currentPhase={currentPhase}
          onNext={(stageAnswers) => {
            Object.entries(stageAnswers).forEach(([key, value]) => setAnswer(key, value as AnswerValue))
            onContinue()
          }}
        />
      )
    case 'basics':
      {
        const basicsInitialValue =
          (initialValue as { date_of_birth?: string; sex_at_birth?: string } | undefined) || {}
        const resolvedBasicsInitialValue = {
          date_of_birth:
            basicsInitialValue.date_of_birth ||
            (answers.date_of_birth as string | undefined),
          sex_at_birth:
            basicsInitialValue.sex_at_birth ||
            (answers.sex_at_birth as string | undefined),
        }

      return (
        <BasicsTemplate
          stage={stage}
          initialValue={resolvedBasicsInitialValue}
          currentPhase={currentPhase}
          onSelect={(value) => {
            handleSelect(value)
            handleSelectObject(value)
            onContinue()
          }}
        />
      )
      }
    case 'bmi_calculator':
      return (
        <BmiCalculatorTemplate
          stage={stage}
          initialValue={initialValue as { height_feet?: number; height_inches?: number; weight_lbs?: number }}
          currentPhase={currentPhase}
          onSelect={(value) => {
            handleSelect(value)
            handleSelectObject(value)
            onContinue()
          }}
        />
      )
    case 'bmi_education': {
      const bmiData = answers.bmi_data as { calculated_bmi?: number } | undefined
      return (
        <BmiEducationTemplate
          stage={stage}
          bmi={bmiData?.calculated_bmi || 0}
          currentPhase={currentPhase}
          onSelect={(value) => {
            handleSelectObject(value)
            onContinue()
          }}
        />
      )
    }
    case 'comorbidity':
      return (
        <ComorbidityTemplate
          stage={stage}
          initialValue={initialValue as string[]}
          currentPhase={currentPhase}
          onSelect={(value) => {
            handleSelectObject(value)
            onContinue()
          }}
        />
      )
    case 'goals':
      {
        const resolvedGoalsInitialValue = Array.isArray(answers.goals_selected)
          ? (answers.goals_selected as string[])
          : Array.isArray(initialValue)
            ? (initialValue as string[])
            : typeof initialValue === 'string' && initialValue
              ? [initialValue]
              : []

      return (
        <GoalsTemplate
          stage={stage}
          initialValue={resolvedGoalsInitialValue}
          currentPhase={currentPhase}
          onSelect={(value) => {
            handleSelectObject(value)
            onContinue()
          }}
        />
      )
      }
    case 'life_context':
      return (
        <LifeContextTemplate
          stage={stage}
          initialValue={initialValue as string[]}
          currentPhase={currentPhase}
          onSelect={(value) => {
            handleSelectObject(value)
            onContinue()
          }}
          onSkip={onContinue}
        />
      )
    case 'therapy_preview':
      return (
        <TherapyPreviewTemplate
          stage={stage}
          answers={answers}
          guidanceData={guidanceData as GuidanceResult}
          currentPhase={currentPhase}
          onContinue={onContinue}
        />
      )
    case 'email_capture':
      return (
        <EmailCaptureTemplate
          stage={stage}
          currentPhase={currentPhase}
          onSelect={(value) => {
            handleSelectObject(value)
            onContinue()
          }}
          onSkip={onContinue}
        />
      )
    case 'safety_screening':
      return (
        <SafetyScreeningTemplate
          stage={stage}
          currentPhase={currentPhase}
          savedAnswers={answers as Record<string, unknown>}
          onSelect={(value) => {
            handleSelectObject(value)
            onContinue()
          }}
        />
      )
    case 'past_glp1':
      return (
        <PastGlp1Template
          stage={stage}
          answers={answers}
          currentPhase={currentPhase}
          onSelect={(value) => {
            handleSelectObject(value)
            onContinue()
          }}
        />
      )
    case 'smart_medication_picker':
      return (
        <SmartMedicationPickerTemplate
          stage={stage}
          answers={answers}
          onFieldChange={(fieldId, value) => {
            setAnswer(fieldId, value)
            onContinue()
          }}
        />
      )
    case 'medication_allergies_picker':
      return (
        <MedicationAllergiesPickerTemplate
          stage={stage}
          answers={answers}
          onFieldChange={(fieldId, value) => {
            setAnswer(fieldId, value)
            onContinue()
          }}
        />
      )
    case 'medicine_selection':
      return (
        <MedicineSelectionTemplate
          stage={stage}
          initialValue={(initialValue as { product?: string })?.product}
          onSelect={(value) => {
            handleSelect(value)
            onContinue()
          }}
        />
      )
    case 'address_form':
      return (
        <AddressFormTemplate
          stage={stage}
          answers={answers}
          currentPhase={currentPhase}
          onSelect={handleSelect}
        />
      )
    case 'id_upload':
      return (
        <IdUploadTemplate
          stage={stage}
          patientId={(answers.patient_id as string) || (answers.patientId as string)}
          existingKey={
            (answers.id_photo_key as string) ||
            (answers.idPhotoS3Key as string) ||
            null
          }
          onSelect={(value) => {
            handleSelectObject(value)
            onContinue()
          }}
        />
      )
    case 'body_photo':
      return (
        <BodyPhotoTemplate
          stage={stage}
          patientId={(answers.patient_id as string) || (answers.patientId as string)}
          existingKey={
            (answers.body_photo_key as string) ||
            (answers.bodyPhotoS3Key as string) ||
            null
          }
          onSelect={(value) => {
            handleSelectObject(value)
            onContinue()
          }}
        />
      )
    case 'review_submit':
      return (
        <ReviewSubmitTemplate
          stage={stage}
          answers={answers}
          currentPhase={currentPhase}
          onContinue={onContinue}
          onSubmit={onSubmit}
        />
      )
    case 'payment_method':
      return (
        <PaymentMethodTemplate
          stage={stage}
          answers={answers}
          currentPhase={currentPhase}
          onSubmit={onSubmit || (async () => {})}
          onSelect={handleSelect}
        />
      )
    default:
      return <div className="py-10 text-center text-white/60">Unknown template: {stage.stageTemplate}</div>
  }
}
